import { useCallback, useEffect, useRef, useState } from 'react';

const SW_URL = `${process.env.PUBLIC_URL || ''}/sw.js`;

function trackWaitingWorker(registration, onUpdate) {
  if (!registration.waiting || !navigator.serviceWorker.controller) {
    return;
  }
  onUpdate(registration.waiting);
}

export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const waitingWorkerRef = useRef(null);
  const registrationRef = useRef(null);
  const shouldReloadRef = useRef(false);

  const showUpdate = useCallback((worker) => {
    waitingWorkerRef.current = worker;
    setUpdateAvailable(true);
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  const applyUpdate = useCallback(() => {
    const worker = waitingWorkerRef.current;
    if (!worker) return;

    setIsApplying(true);
    shouldReloadRef.current = true;
    worker.postMessage({ type: 'SKIP_WAITING' });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return undefined;
    }

    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => registration?.unregister())
        .catch(() => {});
      return undefined;
    }

    const onControllerChange = () => {
      if (!shouldReloadRef.current) return;
      window.location.reload();
    };

    const onUpdateFound = (registration) => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          showUpdate(registration.waiting);
        }
      });
    };

    const checkForUpdates = () => {
      registrationRef.current?.update().catch(() => {});
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    document.addEventListener('visibilitychange', onVisibilityChange);

    navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => {
        registrationRef.current = registration;
        trackWaitingWorker(registration, showUpdate);
        registration.addEventListener('updatefound', () => onUpdateFound(registration));
        checkForUpdates();
      })
      .catch((error) => {
        console.warn('Registrazione service worker non riuscita:', error);
      });

    const intervalId = window.setInterval(checkForUpdates, 60 * 60 * 1000);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [showUpdate]);

  return {
    updateAvailable,
    isApplying,
    applyUpdate,
    dismissUpdate,
  };
}
